import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchAllDeviceMeasurements, fetchCurrentData } from '../utils/api'
import { deviceNames } from '../utils/deviceDefinitions'

// Fetches only the current device data (Stage 1)
async function fetchCurrentDeviceDataForQuery({ signal }) {
  const measurementsByDevice = {} // device -> Set<measurement>

  const processApiResponse = (response, targetMap) => {
    response?.data?.forEach(({ device, measurement }) => {
      if (device && measurement && deviceNames[device]) {
        targetMap[device] ||= new Set()
        targetMap[device].add(measurement)
      }
    })
  }

  const currentDataResponse = await fetchCurrentData(signal)
  processApiResponse(currentDataResponse, measurementsByDevice)

  const sortedDeviceNames = Object.keys(measurementsByDevice).sort((a, b) => a.localeCompare(b))
  const initialDeviceMeasurementsMap = Object.fromEntries(
    sortedDeviceNames.map(device => [device, [...measurementsByDevice[device]].sort()]),
  )

  return {
    deviceMeasurementsMap: initialDeviceMeasurementsMap,
    availableDevices: sortedDeviceNames,
  }
}

// Helper to merge historical data into an existing device measurements map
const processAndCombineData = (currentDeviceMeasurementsMap, historicalApiResponse) => {
    const measurementsByDevice = {} // device -> Set<measurement>

    // Populate with current processed data (from currentDeviceMeasurementsMap)
    if (currentDeviceMeasurementsMap) {
        for (const device in currentDeviceMeasurementsMap) {
            if (Object.hasOwnProperty.call(currentDeviceMeasurementsMap, device) && deviceNames[device]) {
                measurementsByDevice[device] = new Set(currentDeviceMeasurementsMap[device])
            }
        }
    }
    
    // Populate/merge with historical API response data
    if (Array.isArray(historicalApiResponse?.data)) {
        historicalApiResponse.data.forEach(({ device, measurement }) => {
            if (device && measurement && deviceNames[device]) {
                measurementsByDevice[device] ||= new Set()
                measurementsByDevice[device].add(measurement)
            }
        })
    }

    const sortedDeviceNames = Object.keys(measurementsByDevice).sort((a, b) => a.localeCompare(b))
    const finalDeviceMeasurementsMap = Object.fromEntries(
      sortedDeviceNames.map(device => [device, [...measurementsByDevice[device]].sort()]),
    )

    return {
      deviceMeasurementsMap: finalDeviceMeasurementsMap,
      availableDevices: sortedDeviceNames,
    }
}

export function useDeviceMeasurementManagement(initialDevice, initialMeasurement) {
  const [selectedDevices, setSelectedDevices] = useState(initialDevice ? [initialDevice] : [])
  const [selectedMeasurements, setSelectedMeasurements] = useState(initialMeasurement ? [initialMeasurement] : [])

  // Stage 1: Fetch current data
  const {
    data: stageOneData,
    isLoading: stageOneLoading,
    error: stageOneErrorData,
  } = useQuery({
    queryKey: ['currentDeviceMeasurements'], // Distinct queryKey for stage 1
    queryFn: fetchCurrentDeviceDataForQuery,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  // State for the fully combined data from both stages
  const [combinedData, setCombinedData] = useState({
    deviceMeasurementsMap: {},
    availableDevices: [],
  })
  const [stageTwoError, setStageTwoError] = useState(null)

  // Effect to set combinedData based on Stage 1 results
  useEffect(() => {
    if (stageOneData) {
      setCombinedData({
        deviceMeasurementsMap: stageOneData.deviceMeasurementsMap || {},
        availableDevices: stageOneData.availableDevices || [],
      })
    } else if (!stageOneLoading && !stageOneErrorData) {
      // Handle case where stageOneData might be null/undefined post-loading without error
      setCombinedData({ deviceMeasurementsMap: {}, availableDevices: [] })
    }
  }, [stageOneData, stageOneLoading, stageOneErrorData])

  // Stage 2: Fetch historical/all data and merge it with Stage 1 data
  useEffect(() => {
    // Run after Stage 1 has completed (either successfully or with an error)
    if (!stageOneLoading) {
      const controller = new AbortController()
      const {signal} = controller

      fetchAllDeviceMeasurements(signal)
        .then(historicalDataResponse => {
          // Merge historical data with the data currently in combinedData (which is from stage 1)
          setCombinedData(prevCombinedData =>
            processAndCombineData(prevCombinedData.deviceMeasurementsMap, historicalDataResponse),
          )
          setStageTwoError(null) // Clear previous stage two error if successful
        })
        .catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Error fetching all device measurements (Stage 2):', error)
            setStageTwoError(error.message || 'Failed to fetch historical device data')
            // If stage 2 fails, combinedData retains stage 1 data (or its last valid state).
          }
        })

      return () => {
        controller.abort() // Cleanup on unmount or if dependencies change
      }
    }
  }, [stageOneLoading, stageOneData]) // Rerun if stageOneLoading changes or stageOneData is updated (e.g., refetch)

  const deviceMeasurementsMap = useMemo(() => combinedData.deviceMeasurementsMap, [combinedData])
  const availableDevices = useMemo(() => combinedData.availableDevices, [combinedData])
  
  // devicesLoading reflects the loading state of Stage 1 primarily.
  const devicesLoading = stageOneLoading 
  // Combine errors from both stages.
  const devicesError = stageOneErrorData?.message || stageTwoError || null

  const finalAvailableMeasurements = useMemo(() => {
    if (devicesLoading || selectedDevices.length === 0 || Object.keys(deviceMeasurementsMap).length === 0) {
      return []
    }
    const measurements = selectedDevices.flatMap(device => deviceMeasurementsMap[device] || [])
    return [...new Set(measurements)].sort()
  }, [selectedDevices, deviceMeasurementsMap, devicesLoading])

  useEffect(() => {
    if (devicesLoading) return // Wait for initial load to complete

    const newSelectedMeasurements = selectedMeasurements.filter(sm => finalAvailableMeasurements.includes(sm))

    if (newSelectedMeasurements.length !== selectedMeasurements.length ||
        newSelectedMeasurements.some((val, index) => val !== selectedMeasurements[index])) {
      setSelectedMeasurements(newSelectedMeasurements)
    }
  }, [finalAvailableMeasurements, devicesLoading, selectedMeasurements])

  return {
    selectedDevices,
    setSelectedDevices,
    selectedMeasurements,
    setSelectedMeasurements,
    availableDevices, // Derived from combinedData
    deviceMeasurementsMap, // Derived from combinedData
    devicesLoading, // Reflects Stage 1 loading
    devicesError, // Combined error from Stage 1 or Stage 2
    availableMeasurements: finalAvailableMeasurements,
  }
}
